import { FC, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/Dialog/Dialog'
import { AlertDialogProps } from '@radix-ui/react-alert-dialog'
import { ScrollArea, Tabs } from '@mantine/core'
import { PhotoOwnerCaption, PhotosCarousel } from '@/entities/Photos'
import { FullTextDescriptionModal } from '@/shared/ui/ModalDialogs'
import { useTranslation } from 'react-i18next'
import { CollectionsTable } from '../UserOverview/Collections/CollectionsTable'
import { EventsTable } from '../UserOverview/EventsTable/EventsTable'
import { LandingEventsTable } from '../UserOverview/LandingEvents/LandingEventsTable'
import { SessionHistoryTable } from '@/entities/SessionHistory'
import { ActionsTable } from '../UserOverview/UserActions/ActionsTable'

type Props = {
  dialogProps: AlertDialogProps
  currentUserId: number
  onCloseModal: () => void
}

enum PresentationsTabs {
  Events = 'Events',
  Collections = 'Collections',
  SessionHistory = 'SessionHistory',
  Landing = 'Landing',
  Actions = 'Actions',
}

export type CurrentPresentationTableRow = {
  id: number
  photos: Record<string, number>[]
  owner: PhotoOwnerCaption
}

export const UserPresentationsModal: FC<Props> = props => {
  const { dialogProps, currentUserId } = props
  const [currentPresentationsTab, setCurrentPresentationsTab] = useState<PresentationsTabs>(
    PresentationsTabs.Events,
  )
  const [currentRow, setCurrentRow] = useState<CurrentPresentationTableRow | null>(null)
  const [openPhotos, setOpenPhotos] = useState(false)
  const [showMoreDialogOpen, setShowMoreDialogOpen] = useState(false)
  const [currentDescription, setCurrentDescription] = useState('')
  const { t } = useTranslation(['userManagement'])

  const handlePhotosCloseModal = () => {
    setCurrentRow(null)
    setOpenPhotos(false)
  }

  const handleOpenPhotos = (row: CurrentPresentationTableRow) => {
    setCurrentRow(row)
    setOpenPhotos(true)
  }

  const onOpenChange = (open: boolean) => {
    dialogProps?.onOpenChange?.(open)
    setCurrentPresentationsTab(PresentationsTabs.Events)
  }

  const handleOpenFullDescription = (fullText: string) => {
    setCurrentDescription(fullText)
    setShowMoreDialogOpen(true)
  }

  return (
    <Dialog {...dialogProps} onOpenChange={onOpenChange} modal={false}>
      <DialogContent className={'h-[100vh] max-w-[100%] w-[100%] flex flex-col p-0'}>
        <ScrollArea type='always' offsetScrollbars className='p-5 h-full w-full'>
          <DialogHeader>
            <DialogTitle>{t('userManagement:user_overview_title')}</DialogTitle>
            <Tabs
              value={currentPresentationsTab}
              onTabChange={value => setCurrentPresentationsTab(value as PresentationsTabs)}
              variant='pills'
              classNames={{
                tabsList:
                  'flex flex-nowrap overflow-x-auto w-min-content pl-0 bg-white pt-4 pb-4 px-8 border-b border-white-600 bg-white hide-scroll',
                tabLabel: 'font-medium',
                tab: 'data-[active=true]:bg-blue data-[active=true]:hover:bg-blue/90',
              }}
            >
              <Tabs.List>
                <Tabs.Tab value={PresentationsTabs.Events}>{t('userManagement:events')}</Tabs.Tab>
                <Tabs.Tab value={PresentationsTabs.Collections}>
                  {t('userManagement:collections')}
                </Tabs.Tab>
                <Tabs.Tab value={PresentationsTabs.SessionHistory}>
                  {t('userManagement:session_history')}
                </Tabs.Tab>
                <Tabs.Tab value={PresentationsTabs.Landing}>{t('userManagement:landing')}</Tabs.Tab>
                <Tabs.Tab value={PresentationsTabs.Actions}>{t('userManagement:actions')}</Tabs.Tab>
              </Tabs.List>
            </Tabs>
          </DialogHeader>

          {currentPresentationsTab === PresentationsTabs.Events && (
            <EventsTable currentUserId={currentUserId} />
          )}
          {currentPresentationsTab === PresentationsTabs.Collections && (
            <CollectionsTable currentUserId={currentUserId} />
          )}
          {currentPresentationsTab === PresentationsTabs.SessionHistory && (
            <SessionHistoryTable currentUserId={currentUserId} />
          )}
          {currentPresentationsTab === PresentationsTabs.Landing && (
            <LandingEventsTable currentUserId={currentUserId} />
          )}
          {currentPresentationsTab === PresentationsTabs.Actions && (
            <ActionsTable currentUserId={currentUserId} />
          )}
          <PhotosCarousel
            isOpened={openPhotos}
            obj_id={currentRow?.id || 0}
            owner={currentRow?.owner || PhotoOwnerCaption.Residential}
            setIsOpened={setOpenPhotos}
            onCloseModal={handlePhotosCloseModal}
            // @ts-ignore
            photos={currentRow?.photos || []}
          />
          <FullTextDescriptionModal
            dialogProps={{ open: showMoreDialogOpen, onOpenChange: setShowMoreDialogOpen }}
            description={currentDescription}
          />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
